import React, { useEffect, useState } from "react";
import UserByPhoneInput, { ResolvedUser } from "../../../../shared/UserByPhoneInput/UserByPhoneInput";
import "./styles/OwnerSelector.css";

type OwnerMode = "me" | "none" | "someone";

interface OwnerSelectorProps {
  /** Current user's id — selected by default ("Me"). */
  currentUserId: string;
  /** Current ownerId on the business (undefined for create mode). */
  value: string | null | undefined;
  /** If the current owner is someone else, the parent can pre-fill the chip. */
  initialResolvedOwner?: ResolvedUser | null;
  onChange: (ownerId: string | null) => void;
  /** When true, all controls are disabled (e.g. effective owner is not the user). */
  disabled?: boolean;
  disabledHint?: string;
  /** When true, skips the Me/No owner/Someone else toggle and always renders
   *  the phone-lookup field — for flows where the person filling the form is
   *  never the owner (e.g. a captain submitting a business on someone else's
   *  behalf). `currentUserId` is unused in this mode. */
  forceSomeoneElse?: boolean;
  /** Icon for the phone-lookup's confirm button — forwarded to UserByPhoneInput. */
  confirmIcon?: React.ReactNode;
}

const OwnerSelector: React.FC<OwnerSelectorProps> = ({
  currentUserId,
  value,
  initialResolvedOwner,
  onChange,
  disabled = false,
  disabledHint,
  forceSomeoneElse = false,
  confirmIcon,
}) => {
  // Derive initial mode from the incoming value.
  const initialMode: OwnerMode = forceSomeoneElse
    ? "someone"
    : value === undefined || value === currentUserId
      ? "me"
      : value === null
      ? "none"
      : "someone";

  const [mode, setMode] = useState<OwnerMode>(initialMode);
  const [resolvedOwner, setResolvedOwner] = useState<ResolvedUser | null>(
    initialResolvedOwner ?? null
  );

  // Keep parent in sync when mode changes (without nuking a resolved chip).
  useEffect(() => {
    if (mode === "me") onChange(currentUserId);
    else if (mode === "none") onChange(null);
    else if (mode === "someone" && resolvedOwner) onChange(resolvedOwner.userId);
    else if (mode === "someone" && !resolvedOwner) onChange(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const handleResolved = (user: ResolvedUser) => {
    setResolvedOwner(user);
    onChange(user.userId);
  };

  const handleCleared = () => {
    setResolvedOwner(null);
    onChange(null);
  };

  return (
    <div className={`owner-selector ${disabled ? "is-disabled" : ""}`}>
      {!forceSomeoneElse && (
        <div className="owner-selector__modes">
          {(["me", "none", "someone"] as OwnerMode[]).map((m) => (
            <label key={m} className={`owner-selector__option ${mode === m ? "is-active" : ""}`}>
              <input
                type="radio"
                name="owner-mode"
                value={m}
                checked={mode === m}
                disabled={disabled}
                onChange={() => setMode(m)}
              />
              <span>
                {m === "me" ? "Me" : m === "none" ? "No owner" : "Someone else"}
              </span>
            </label>
          ))}
        </div>
      )}

      {(mode === "someone" || forceSomeoneElse) && !disabled && (
        <UserByPhoneInput
          resolved={resolvedOwner}
          onResolved={handleResolved}
          onCleared={handleCleared}
          placeholder="Owner's 10-digit phone number"
          confirmIcon={confirmIcon}
        />
      )}

      {disabled && disabledHint && (
        <p className="owner-selector__hint">{disabledHint}</p>
      )}
    </div>
  );
};

export default OwnerSelector;
